
// src/components/CVUpload.jsx
import { useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useApiOperations } from '@utils/api-functions';
import { useApi } from '@utils/useApi';
import { API_BASE_URL } from '@config';
import FileUploadSection from './FileUploadSection';
import ProcessingStepsDisplay from './ProcessingStepsDisplay';
import ExperienceLayout from '../experience/ExperienceLayout'; // Import shared layout component
import {
  // Upload, FileText, // Icons likely used within child components
  AlertCircle // Used in main error display
} from 'lucide-react';
import {
  Card,
  CardContent
} from '@shared/forms/FormStyles';
import { debug } from '@utils/debug'; // Import debug utility

// Note: Child components like FileUploadSection, ProcessingStepsDisplay, ExtractedRolesSection handle their own imports/definitions.

// --- Main Component ---

/**
 * Component for uploading a CV, processing it via the backend,
 * displaying extracted data, allowing edits, and saving the updated profile.
 * @param {object} props - Component props.
 * @param {function} props.onUploadComplete - Callback function triggered when the profile save is complete.
 * @returns {JSX.Element} The CV Upload component.
 */
const CVUpload = ({ onUploadComplete }) => {
  const { uploadInitiate, saveImportedProfile } = useApiOperations();
  const callApi = useApi();
  const navigate = useNavigate(); // Get navigate function
  const [file, setFile] = useState(null);
  // Use underscore prefix to indicate intentionally unused variable
  // eslint-disable-next-line no-unused-vars
  const [processingId, setProcessingId] = useState(null); // State for Stage 2 ID - used in SSE processing
  const sseAbortControllerRef = useRef(null); // Ref to manage SSE fetch abortion
  // Initialize processing steps state - keys cover potential backend strategies
  // Define initial steps based on the new flow
  const initialProcessingSteps = {
    initiate: 'waiting', // New step for Stage 1
    processing: 'waiting', // Generic step for Stage 2 (details filled by strategy_info)
    complete: 'waiting'
  };
  const [processingSteps, setProcessingSteps] = useState(initialProcessingSteps);
  const [currentStepConfig, setCurrentStepConfig] = useState([]); // State to hold the steps for the current strategy
  const [error, setError] = useState(null);
  const [extractedData, setExtractedData] = useState({
    personalStatement: null,
    education: [],
    roles: [],
    globalSkills: [], // Re-introduce globalSkills
    rawCvText: null,
  });
  // Use underscore prefix to indicate intentionally unused variable
  const [validatedRoles, setValidatedRoles] = useState([]);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [saveErrors, setSaveErrors] = useState([]);
  const [successMessage, setSuccessMessage] = useState(null);

  /**
   * Performs basic validation on the data extracted from the CV.
   * Currently checks if roles array is valid.
   * @param {object} data - The extracted data object.
   * @returns {Array<string>} An array of validation error messages, empty if valid.
   * @private
   */
  const validateExtractedData = (data) => {
    const errors = [];
    if (!data.roles || !Array.isArray(data.roles)) {
      errors.push("No valid roles were extracted from the CV");
    } else if (data.roles.length === 0) {
      // Allow saving even if no roles were extracted, as other data might be present
    }
    return errors;
  };

  /**
   * Handles saving the (potentially edited) extracted profile data to the backend.
   * Calls the `saveImportedProfile` API operation.
   * Updates state for loading indicators, errors, and success messages.
   * Resets component state on successful save.
   */
  const handleSaveProfile = async () => {
    setIsSavingProfile(true);
    setSaveErrors([]);
    setSuccessMessage(null);

    try {
      debug('cvupload', "Saving extracted profile data:", extractedData);

      // Call the actual API endpoint
      // Ensure extractedData contains { personalStatement, education, roles, globalSkills }
      const saveData = {
        personalStatement: extractedData.personalStatement,
        education: extractedData.education,
        roles: extractedData.roles,
        globalSkills: extractedData.globalSkills || [], // Add globalSkills to save payload
      };

      const response = await saveImportedProfile(saveData); // Call the API function

      debug('cvupload', "Profile data saved successfully via API:", response);

      setSuccessMessage(response?.message || "Profile successfully updated from CV!");
      onUploadComplete({ success: true }); // Notify parent

      // Reset state after successful save
      setExtractedData({ personalStatement: null, education: [], roles: [], globalSkills: [], rawCvText: null }); // Reset globalSkills too
      setValidatedRoles([]);
      setFile(null);
      // Consider if processing steps should be reset here or kept visible.

      // Navigate to experience page after successful save
      navigate('/experience'); // Assuming '/experience' is the correct path
    } catch (error) {
      debug('cvupload:error', 'Error saving profile:', error);
      // Extract more specific error message if available from API response
      const apiErrorMessage = error.response?.data?.error?.message || error.message || 'Unknown error';
      setSaveErrors([`Failed to save profile: ${apiErrorMessage}`]);
      // Optionally notify parent of failure: onUploadComplete({ success: false, error: apiErrorMessage });
    } finally {
      setIsSavingProfile(false);
    }
  };


  /**
   * Handles the file input change event.
   * Validates the selected file type and updates the file state.
   * Resets processing steps and extracted data.
   * @param {React.ChangeEvent<HTMLInputElement>} e - The input change event.
   */
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && (
      selectedFile.type === 'application/pdf' ||
      selectedFile.type === 'application/msword' ||
      selectedFile.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    )) {
      setFile(selectedFile);
      setError(null); // Keep setError
      // Reset processing steps state using the keys from the current state object
      const initialStepKeys = Object.keys(processingSteps);
      const resetSteps = initialStepKeys.reduce((acc, key) => ({ ...acc, [key]: 'waiting' }), {});
      setProcessingSteps(resetSteps); // Use setProcessingSteps directly
      setCurrentStepConfig([]); // Clear dynamic step config
      setExtractedData({ personalStatement: null, education: [], roles: [], globalSkills: [], rawCvText: null }); // Reset globalSkills too
      setSuccessMessage(null);
      setSaveErrors([]);
    }
  };

  /**
   * Initiates the CV upload and extraction process.
   * Calls the `uploadCVAndExtract` API operation with a progress callback.
   * Updates the processing steps state based on SSE events received.
   * Handles errors during the upload and extraction process.
   * Validates and sets the extracted data upon successful completion.
   */
  // Renamed from handleUpload to reflect Stage 1
  const handleInitiateUpload = async () => {
    if (!file) return;
    // Reset states for new attempt
    setError(null);
    setSuccessMessage(null);
    setSaveErrors([]);
    setProcessingId(null);
    setExtractedData({ personalStatement: null, education: [], roles: [], globalSkills: [], rawCvText: null });
    setCurrentStepConfig([]); // Clear old step config
    setProcessingSteps({ ...initialProcessingSteps, initiate: 'processing' }); // Start initiate step

    // Abort previous SSE connection if it exists
    if (sseAbortControllerRef.current) {
        sseAbortControllerRef.current.abort();
        sseAbortControllerRef.current = null;
        debug('cvupload:sse', 'Aborted previous SSE connection.');
    }



    try {
        // --- Stage 1: Initiate Upload ---
        const initiateResponse = await uploadInitiate(file);
        debug('cvupload:initiate', 'Initiate response received:', initiateResponse);

        if (initiateResponse.status === 'complete') {
            // Cache Hit
            setProcessingSteps({ ...initialProcessingSteps, initiate: 'complete', processing: 'complete', complete: 'complete' });
            setExtractedData({ // Assuming cachedResult has the same structure
                personalStatement: initiateResponse.cachedResult?.personalStatement || null,
                education: initiateResponse.cachedResult?.education || [],
                roles: initiateResponse.cachedResult?.roles || [],
                globalSkills: initiateResponse.cachedResult?.globalSkills || [],
                rawCvText: initiateResponse.cachedResult?.rawCvText || null, // May not be cached
            });
            setValidatedRoles(new Array(initiateResponse.cachedResult?.roles?.length || 0).fill(true));
            setSuccessMessage(initiateResponse.message || "CV processed from cache.");
            setProcessingId(initiateResponse.processingId || null); // Store original ID if available

        } else if (initiateResponse.status === 'pending') {
            // Processing Needed
            setProcessingSteps({ ...initialProcessingSteps, initiate: 'complete', processing: 'processing' });
            setProcessingId(initiateResponse.processingId);
            // --- Stage 2: Start SSE Processing ---
            await startSseProcessing(initiateResponse.processingId);

        } else {
            // Handle unexpected status from /initiate
            throw new Error(initiateResponse.message || 'Unexpected response from initiation step.');
        }

    } catch (err) {
        debug('cvupload:error', 'Initiate/Upload error:', err);
        const message = err.message || 'An unknown error occurred during CV initiation.';
        if (err.code === 'ECONNREFUSED' && err.address === '127.0.0.1' && err.port === 10000) {
             setError(`Connection refused. Is the local storage emulator (Azurite) running? Please start it and try again. (${message})`);
        } else {
             setError(message);
        }
        // Mark all steps as error on initiation failure
        setProcessingSteps(prev => Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: 'error' }), {}));
    }
  };

  // New function to handle Stage 2 SSE connection
  const startSseProcessing = async (procId) => {
      if (!procId) {
          setError("Missing processing ID to start analysis.");
          setProcessingSteps(prev => ({ ...prev, processing: 'error', complete: 'error' }));
          return;
      }
      debug('cvupload:sse', `Starting SSE processing for ID: ${procId}`);

      // Create an AbortController for this fetch request
      const controller = new AbortController();
      sseAbortControllerRef.current = controller;

      try {
          // Use callApi to handle auth and make the GET request for SSE
          // We pass a new onProgress handler specific to Stage 2 events
          const finalData = await callApi(
              `${API_BASE_URL}/api/v1/cv/process/${procId}`,
              {
                  method: 'GET',
                  signal: controller.signal, // Pass the signal
                  onProgress: (progressUpdate) => { // Stage 2 onProgress handler
                      debug('cvupload:sse:onProgress', "Stage 2 Progress Update:", progressUpdate);
                      try {
                          if (progressUpdate.type === 'strategy_info') {
                              const { steps } = progressUpdate.payload;
                              if (steps && Array.isArray(steps)) {
                                  setCurrentStepConfig(steps);
                                  // Re-initialize processing steps based on actual strategy steps
                                  setProcessingSteps(() => { // Removed unused 'prev'
                                      const strategySteps = steps.reduce((acc, step) => ({ ...acc, [step.key]: 'waiting' }), {});
                                      // Keep initiate complete, mark first strategy step as processing
                                      const firstStrategyStepKey = steps[0]?.key;
                                      if (firstStrategyStepKey) strategySteps[firstStrategyStepKey] = 'processing';
                                      return { initiate: 'complete', ...strategySteps, complete: 'waiting' };
                                  });
                              }
                          } else if (progressUpdate.type === 'stage') {
                              const stage = progressUpdate.payload;
                              setProcessingSteps(prevSteps => {
                                  let newSteps = { ...prevSteps };
                                  let previousStepKey = null;
                                  // Find the current 'processing' step to mark it complete
                                  for (const key of Object.keys(newSteps)) {
                                      if (newSteps[key] === 'processing') {
                                          previousStepKey = key;
                                          break;
                                      }
                                  }
                                  if (previousStepKey && newSteps[previousStepKey]) {
                                      newSteps[previousStepKey] = 'complete';
                                  }
                                  // Mark the new stage as 'processing' if it exists
                                  if (newSteps[stage] === 'waiting') {
                                      newSteps[stage] = 'processing';
                                  } else {
                                       debug('cvupload:sse:warn', `Received stage '${stage}' but it was not in 'waiting' state or not found in config.`);
                                  }
                                  return newSteps;
                              });
                          } else if (progressUpdate.type === 'warning') {
                              // Optionally display warnings to the user (e.g., using a toast notification)
                              debug('cvupload:sse:warn', 'Received warning:', progressUpdate.payload);
                              // Example: setWarningMessage(progressUpdate.payload.message);
                          } else if (progressUpdate.type === 'error') {
                              setError(progressUpdate.payload.message || 'An error occurred during backend processing.');
                              setProcessingSteps(prevSteps => {
                                  let newSteps = { ...prevSteps };
                                  const errorStage = progressUpdate.payload.stage;
                                  let stepToMarkError = Object.keys(newSteps).find(key => newSteps[key] === 'processing');
                                  if (errorStage && newSteps[errorStage]) stepToMarkError = errorStage;
                                  if (stepToMarkError) newSteps[stepToMarkError] = 'error';
                                  newSteps.complete = 'error'; // Mark final step as error
                                  return newSteps;
                              });
                              // Abort the fetch request on error
                              if (sseAbortControllerRef.current) {
                                  sseAbortControllerRef.current.abort();
                                  sseAbortControllerRef.current = null;
                              }
                          }
                          // Note: 'final_data' is handled by the resolved promise of callApi
                      } catch (callbackError) {
                          debug('cvupload:sse:onProgress:error', 'Error inside Stage 2 onProgress callback:', callbackError);
                          setError(`Internal error processing update: ${callbackError.message}`);
                          setProcessingSteps(prev => ({ ...prev, processing: 'error', complete: 'error' }));
                           if (sseAbortControllerRef.current) {
                                sseAbortControllerRef.current.abort();
                                sseAbortControllerRef.current = null;
                           }
                      }
                  } // End of onProgress for Stage 2
              }
          ); // End callApi

          // If callApi resolves successfully, it means 'final_data' was received
          debug('cvupload:sse', 'SSE stream finished, final data received:', finalData);
          setProcessingSteps(prev => { // Mark all steps complete
                let finalSteps = { ...prev };
                Object.keys(finalSteps).forEach(key => {
                    if (finalSteps[key] !== 'error') finalSteps[key] = 'complete';
                });
                return finalSteps;
          });

          // Validate and set final data
          const validationErrors = validateExtractedData(finalData);
          if (validationErrors.length > 0) {
              setError(validationErrors.join('. '));
              setProcessingSteps(prev => ({ ...prev, complete: 'error' }));
          } else {
              setExtractedData({
                  personalStatement: finalData.personalStatement || null,
                  education: finalData.education || [],
                  roles: finalData.roles || [],
                  globalSkills: finalData.globalSkills || [],
                  rawCvText: finalData.rawCvText || null,
              });
              setValidatedRoles(new Array(finalData.roles?.length || 0).fill(true));
          }

      } catch (err) {
          // Catch errors from callApi itself (e.g., network error, auth error, stream_incomplete)
          debug('cvupload:sse:error', 'Error during SSE fetch/processing:', err);
          // Don't setError if it was an AbortError from our own cancellation
          if (err.name !== 'AbortError') {
             setError(err.message || 'An unknown error occurred during CV analysis.');
             setProcessingSteps(prev => { // Mark steps as error
                let errorSteps = { ...prev };
                 Object.keys(errorSteps).forEach(step => {
                     if (errorSteps[step] === 'processing' || errorSteps[step] === 'waiting') {
                         errorSteps[step] = 'error';
                     }
                 });
                 errorSteps.complete = 'error';
                 return errorSteps;
             });
          } else {
             debug('cvupload:sse', 'SSE fetch aborted.');
          }
      } finally {
          // Clear the abort controller ref after fetch completes or errors
          sseAbortControllerRef.current = null;
          debug('cvupload:sse', 'Cleared SSE abort controller ref.');
      }
  };

  // Callback for when a role is edited in ExtractedRoleCard via ExtractedRolesSection
  /**
   * Updates the state for a specific role when edited in a child component.
   * @param {number} index - The index of the role being edited.
   * @param {object} updatedRole - The updated role data.
   */
  const handleEditRole = (index, updatedRole) => {
    setExtractedData(prev => {
      const newRoles = [...prev.roles];
      newRoles[index] = updatedRole;
      return { ...prev, roles: newRoles };
    });
  };

  // Callback for when a role's validation status changes in ExtractedRoleCard via ExtractedRolesSection
  /**
   * Updates the validation status for a specific role based on input from a child component.
   * @param {number} index - The index of the role being validated.
   * @param {boolean} isValid - The validation status.
   */
  const handleValidateRole = useCallback((index, isValid) => {
    setValidatedRoles(prevValidatedRoles => {
      const newValidated = [...prevValidatedRoles];
      if (index < newValidated.length) { // Add bounds check
        newValidated[index] = isValid;
      }
      return newValidated;
    });
  }, []); // Dependency array is empty as setValidatedRoles is stable

  // Handler for saving edited personal statement
  /**
   * Updates the personal statement in the component's state.
   * @param {string} newStatement - The updated personal statement text.
   */
  const handleSavePersonalStatement = (newStatement) => {
    setExtractedData(prev => ({ ...prev, personalStatement: newStatement }));
    // TODO(developer, 2025-07-04): Consider adding temporary success indicator for inline edits
  };

  // handleSaveGlobalSkills removed as the component is gone

   // Handler for deleting a role
  /**
   * Removes a role from the extracted data state based on its index.
   * Note: This only affects the frontend state before saving.
   * @param {number} indexToDelete - The index of the role to remove.
   */
  const handleDeleteRole = (indexToDelete) => {
    // TODO(developer, 2025-07-04): Consider adding confirmation dialog before deleting role
    debug('cvupload', `Deleting role at index: ${indexToDelete}`);
    setExtractedData(prev => ({
      ...prev,
      roles: prev.roles.filter((_, index) => index !== indexToDelete)
    }));
    setValidatedRoles(prev => prev.filter((_, index) => index !== indexToDelete));
     // Note: Role is only removed from local state here. Backend handles final state on save.
  };

  // --- Render Logic ---
  return (
    <div className="space-y-6">
      {/* --- Upload Card --- */}
      <Card>
        <div className="border-b border-huntd-secondary/10 bg-white px-6 py-4">
          <h3 className="text-2xl font-bold text-huntd-primary">Upload CV</h3>
          <p className="mt-1 text-sm text-huntd-secondary">Upload your CV to automatically extract profile information</p>
        </div>
        <CardContent>
          {/* Use FileUploadSection component */}
          <FileUploadSection
            file={file}
            processingSteps={processingSteps}
            handleFileChange={handleFileChange}
            handleUpload={handleInitiateUpload}
            setFile={setFile}
          />

          {/* Use ProcessingStepsDisplay component */}
          <ProcessingStepsDisplay
            fileSelected={!!file}
            processingSteps={processingSteps}
            stepConfig={currentStepConfig}
          />

          {/* General Error Display - Keep here as it relates to upload/processing */}
          {error ? (
            <div className="p-4 bg-huntd-error/10 border border-huntd-error/20 rounded-lg flex items-start mt-4">
              <AlertCircle className="w-5 h-5 text-huntd-error mt-0.5" />
              <div className="ml-3">
                <h3 className="text-base font-medium text-huntd-error">Error Processing CV</h3>
                <p className="mt-1 text-base text-huntd-error/80">{error}</p>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* --- Display Extracted Data using shared ExperienceLayout --- */}
      {processingSteps.complete === 'complete' && !error && (
        <Card>
          <CardContent>
            <ExperienceLayout
              personalStatement={extractedData.personalStatement}
              education={extractedData.education}
              roles={extractedData.roles}
              skills={extractedData.globalSkills}
              projects={[]}
              onSavePersonalStatement={handleSavePersonalStatement}
              onEditRole={handleEditRole}
              onValidateRole={handleValidateRole}
              onDeleteRole={handleDeleteRole}
              validatedRoles={validatedRoles} // Pass validatedRoles to fix ESLint error
              saveProfile={handleSaveProfile}
              saveErrors={saveErrors}
              successMessage={successMessage}
              isSaving={isSavingProfile}
              showSaveButton={extractedData.roles.length > 0}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

CVUpload.propTypes = {
  onUploadComplete: PropTypes.func.isRequired
};

export default CVUpload;
